import { log } from '@/logger/logger';
import { WeavePricingTier } from '@/types/shared';

const logger = log.getLogger('figmaEmbedToken');

export type FigmaEmbedPermission = 'view' | 'edit';

export interface FigmaEmbedTokenPayload {
  sub: string;
  exp: number;
  iat?: number;
  permission?: FigmaEmbedPermission;
  /**
   * True when the user is community-blocked or sharing-restricted in Figma. `permission`
   * is already downgraded to 'view' when this is true — this claim exists only for the
   * client to pick a restricted-user presentation, never to grant an operation `permission`
   * denies.
   */
  weave_restricted?: boolean;
  recipe_id?: string;
  /**
   * Minted by Sinatra from the *file's* plan — its AI-credits add-on or drawdown
   * state. Drives which conversion ratio the client displays; the Weavy backend
   * reads the same claim to charge, so the two stay consistent by construction.
   */
  weave_pricing_tier?: string;
}

/**
 * Narrows an untrusted `weave_pricing_tier` claim to a {@link WeavePricingTier}.
 *
 * Fails closed to {@link WeavePricingTier.Standard} for anything that isn't exactly
 * the discounted wire value, so a malformed or absent claim shows the full price
 * rather than under-quoting. Mirrors `normalizeWeavePricingTier` in the Weavy
 * backend — keep them in step.
 */
export function normalizeWeavePricingTier(claim: unknown): WeavePricingTier {
  return claim === WeavePricingTier.Discounted ? WeavePricingTier.Discounted : WeavePricingTier.Standard;
}

export function getFigmaEmbedUserId(sub: string): string {
  return `figma:${sub}`;
}

export function decodeFigmaEmbedToken(token: string): FigmaEmbedTokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Base64url → base64, then re-pad to a multiple of 4.
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // `atob` produces a binary string (one char per byte). Route it through a byte array and
    // TextDecoder so any future multi-byte UTF-8 claim (accented Latin, CJK, Arabic, etc.)
    // round-trips correctly. Without this, non-ASCII input comes back as mojibake
    // (e.g., "日本語" → "æ¥æ¬èª"). The current token schema is ASCII-only, but this is a
    // cheap guard against silent corruption if a string claim gets added later.
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder('utf-8').decode(bytes);

    const payload = JSON.parse(json) as FigmaEmbedTokenPayload;
    if (typeof payload?.sub !== 'string' || typeof payload?.exp !== 'number') {
      return null;
    }
    return payload;
  } catch (error) {
    logger.error('Failed to decode Figma embed token payload', error);
    return null;
  }
}

/**
 * Whether the embed token belongs to a logged-out "Anyone with the link" viewer.
 *
 * Figma mints this token (the only one the client ever decodes) in
 * `sinatra/controllers/api/api_weave.rb` with `sub = (user&.id).to_s`, so `sub`
 * is the empty string exactly when there's no Figma user. The token is only
 * minted once file access + the Weave gate pass, so an empty `sub` uniquely
 * identifies an anonymous viewer — a signed-in user always has a non-empty id.
 *
 * This is a deliberate contract pinned by a server-side test
 * (`test_api_weave`); do not relax it to a looser falsy check.
 */
export function isAnonymousEmbedViewer(payload: FigmaEmbedTokenPayload): boolean {
  return payload.sub === '';
}
